import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Loader2, Users, DollarSign, FileText, Sparkles, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiService } from '@/lib/api';
import { hasContractorPaymentReport } from '@/lib/featureFlags';
import { toast } from 'sonner';

const money = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
  { id: 'this-month', label: 'This month', range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), n]; } },
  { id: 'last-month', label: 'Last month', range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; } },
  { id: 'this-quarter', label: 'This quarter', range: () => { const n = new Date(); const q = Math.floor(n.getMonth() / 3) * 3; return [new Date(n.getFullYear(), q, 1), n]; } },
  { id: 'this-year', label: 'This year', range: () => { const n = new Date(); return [new Date(n.getFullYear(), 0, 1), n]; } },
  { id: 'all', label: 'All time', range: () => [null, null] },
];

export default function ContractorPaymentReport({ user }) {
  const navigate = useNavigate();
  const entitled = hasContractorPaymentReport(user);
  const [preset, setPreset] = useState('this-year');
  const [start, setStart] = useState(() => iso(new Date(new Date().getFullYear(), 0, 1)));
  const [end, setEnd] = useState(() => iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    const [s, e] = p.range();
    setStart(s ? iso(s) : '');
    setEnd(e ? iso(e) : '');
  };

  const load = useCallback(async () => {
    if (!entitled) return;
    setLoading(true);
    try {
      const res = await apiService.getContractorPaymentReport(user.id, start || undefined, end || undefined);
      setData(res.data);
    } catch (e) {
      toast.error('Could not load the payment report');
    } finally {
      setLoading(false);
    }
  }, [user?.id, start, end, entitled]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data?.payments?.length) { toast.message('No payments to export'); return; }
    const header = ['Date', 'Contractor', 'Project', 'Phase', 'Method', 'Amount'];
    const rows = data.payments.map((p) => [
      fmtDate(p.date), p.contractor_name, p.project_name, p.phase_name, p.payment_method,
      (Number(p.amount) || 0).toFixed(2),
    ]);
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [header, ...rows, [], ['', '', '', '', 'Total', (Number(data.total_paid) || 0).toFixed(2)]]
      .map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contractor-payments_${start || 'all'}_${end || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const exportPdf = () => {
    if (!data) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to export the PDF'); return; }
    const period = start || end ? `${start ? fmtDate(start) : 'Beginning'} — ${end ? fmtDate(end) : 'Today'}` : 'All time';
    const detailRows = (data.payments || []).map((p) => `<tr>
      <td>${fmtDate(p.date)}</td><td>${p.contractor_name}</td><td>${p.project_name}</td>
      <td>${p.phase_name || ''}</td><td style="text-transform:capitalize;">${p.payment_method || ''}</td>
      <td style="text-align:right;">${money(p.amount)}</td></tr>`).join('');
    const contractorRows = (data.by_contractor || []).map((c) => `<tr>
      <td>${c.contractor_name}</td><td style="text-align:right;">${c.count}</td>
      <td style="text-align:right;">${money(c.total)}</td></tr>`).join('');
    w.document.write(`<!doctype html><html><head><title>Contractor Payment Report</title><meta charset="utf-8"/>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:0;color:#0f172a;}
      .hdr{background:#0f172a;color:#fff;padding:20px 28px;} .hdr h1{margin:0;font-size:20px;} .hdr p{margin:4px 0 0;color:#94a3b8;font-size:13px;}
      .wrap{padding:24px 28px;} h2{font-size:14px;text-transform:uppercase;letter-spacing:.05em;color:#64748b;margin:24px 0 8px;}
      table{width:100%;border-collapse:collapse;} th{text-align:left;font-size:11px;text-transform:uppercase;color:#64748b;border-bottom:2px solid #0f172a;padding:8px;}
      td{padding:7px 8px;border-bottom:1px solid #eee;font-size:13px;} .tot{display:flex;justify-content:space-between;border-top:2px solid #0f172a;margin-top:10px;padding-top:10px;font-weight:800;font-size:16px;}
      </style></head><body>
      <div class="hdr"><h1>Contractor Payment Report</h1><p>${period} · Generated ${new Date().toLocaleDateString()} · DrawReady</p></div>
      <div class="wrap">
        <div class="tot"><span>Total paid (${data.payment_count} payments)</span><span>${money(data.total_paid)}</span></div>
        <h2>By contractor</h2>
        <table><thead><tr><th>Contractor</th><th style="text-align:right;">Payments</th><th style="text-align:right;">Total</th></tr></thead><tbody>${contractorRows}</tbody></table>
        <h2>All payments</h2>
        <table><thead><tr><th>Date</th><th>Contractor</th><th>Project</th><th>Phase</th><th>Method</th><th style="text-align:right;">Amount</th></tr></thead><tbody>${detailRows}</tbody></table>
      </div></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 500);
  };

  if (!entitled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="cpr-upgrade">
          <div className="w-12 h-12 mx-auto rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4"><Lock className="w-6 h-6" /></div>
          <h1 className="text-xl font-extrabold text-slate-900">Contractor Payment Report</h1>
          <p className="text-slate-500 mt-2 text-sm">See exactly what every contractor was paid, and when, across a date range — perfect for handing to accounting. Included with the <span className="font-semibold text-indigo-600">Pro</span> plan.</p>
          <Button onClick={() => navigate('/pricing')} className="mt-5 bg-indigo-600 hover:bg-indigo-700" data-testid="cpr-upgrade-cta">
            <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Pro
          </Button>
          <button onClick={() => navigate('/contractors')} className="block mx-auto mt-4 text-sm text-slate-400 hover:text-slate-600">Back to contractors</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-payment-report">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <button onClick={() => navigate('/contractors')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-1" data-testid="cpr-back"><ArrowLeft className="w-4 h-4" /> Contractors</button>
            <h1 className="text-2xl font-extrabold text-slate-900">Contractor Payment Report</h1>
            <p className="text-sm text-slate-500">What every contractor was paid, and when — export it for accounting.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="cpr-export-csv"><Download className="w-4 h-4 mr-2" /> CSV</Button>
            <Button onClick={exportPdf} data-testid="cpr-export-pdf" className="bg-slate-900 hover:bg-slate-800"><Printer className="w-4 h-4 mr-2" /> PDF</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {PRESETS.map((p) => (
              <button key={p.id} onClick={() => applyPreset(p.id)} data-testid={`cpr-preset-${p.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${preset === p.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">From</label>
              <input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreset('custom'); }} data-testid="cpr-start" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">To</label>
              <input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreset('custom'); }} data-testid="cpr-end" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : !data ? null : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cpr-total">
                <DollarSign className="w-4 h-4 text-emerald-600 mb-1" />
                <div className="text-2xl font-extrabold text-slate-900">{money(data.total_paid)}</div>
                <div className="text-xs text-slate-500">Total paid</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <Users className="w-4 h-4 text-indigo-600 mb-1" />
                <div className="text-2xl font-extrabold text-slate-900">{data.by_contractor?.length || 0}</div>
                <div className="text-xs text-slate-500">Contractors paid</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <FileText className="w-4 h-4 text-slate-600 mb-1" />
                <div className="text-2xl font-extrabold text-slate-900">{data.payment_count}</div>
                <div className="text-xs text-slate-500">Payments</div>
              </div>
            </div>

            {data.payment_count === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500" data-testid="cpr-empty">
                No contractor payments recorded in this period.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By contractor */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cpr-by-contractor">
                  <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">By contractor</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {data.by_contractor.map((c, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`cpr-contractor-row-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-slate-800">{c.contractor_name}</td>
                          <td className="px-2 py-2.5 text-right text-slate-400 text-xs">{c.count} pmt</td>
                          <td className="px-4 py-2.5 text-right font-bold text-slate-900 tabular-nums">{money(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* By project */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cpr-by-project">
                  <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">By project</div>
                  <table className="w-full text-sm">
                    <tbody>
                      {data.by_project.map((p, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`cpr-project-row-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-slate-800">{p.project_name}</td>
                          <td className="px-2 py-2.5 text-right text-slate-400 text-xs">{p.count} pmt</td>
                          <td className="px-4 py-2.5 text-right font-bold text-slate-900 tabular-nums">{money(p.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detail */}
            {data.payment_count > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="cpr-detail">
                <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 text-sm">All payments</div>
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                      <th className="px-4 py-2 font-bold">Date</th>
                      <th className="px-4 py-2 font-bold">Contractor</th>
                      <th className="px-4 py-2 font-bold">Project</th>
                      <th className="px-4 py-2 font-bold">Phase</th>
                      <th className="px-4 py-2 font-bold text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.map((p, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`cpr-payment-row-${i}`}>
                        <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(p.date)}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{p.contractor_name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{p.project_name}</td>
                        <td className="px-4 py-2.5 text-slate-500">{p.phase_name}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-slate-900 tabular-nums">{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
